import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@shared/api';
import { rfqApi } from '../api/rfq-api';
import type { BuyRequestFeedQuery, SubmitQuoteRequest } from './types';

export const rfqKeys = {
  all: ['rfq'] as const,
  feed: (q: BuyRequestFeedQuery) => [...rfqKeys.all, 'feed', q] as const,
  detail: (id: string) => [...rfqKeys.all, 'detail', id] as const,
  myQuotes: (page: number, size: number) => [...rfqKeys.all, 'quotes', page, size] as const,
};

export function useBuyRequestsFeedQuery(query: BuyRequestFeedQuery) {
  return useQuery({
    queryKey: rfqKeys.feed(query),
    queryFn: () => rfqApi.feed(query),
    staleTime: 30_000,
  });
}

// §7 v1.2 — single RFQ. Do not retry on 403/404 (those are terminal access decisions).
export function useBuyRequestQuery(id: string | undefined) {
  return useQuery({
    queryKey: rfqKeys.detail(id ?? ''),
    queryFn: () => rfqApi.get(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 403 || error.status === 404)) return false;
      return failureCount < 2;
    },
  });
}

export function useMyQuotesQuery(page = 0, size = 20) {
  return useQuery({
    queryKey: rfqKeys.myQuotes(page, size),
    queryFn: () => rfqApi.myQuotes(page, size),
    staleTime: 30_000,
  });
}

export function useSubmitQuoteMutation(buyRequestId: string, idempotencyKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitQuoteRequest) =>
      rfqApi.submitQuote(buyRequestId, body, idempotencyKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rfqKeys.all });
    },
  });
}

export function useWithdrawQuoteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rfqApi.withdrawQuote(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rfqKeys.all });
    },
  });
}
